import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Search, UserPlus, X, Check, Loader2, Trash2, ShieldAlert, Clock,
  Mail as MailIcon, Send,
} from "lucide-react";
import { api, apiErr } from "../lib/api";
import { Card, GoldButton, GhostButton } from "./UI";
import { PlayerAvatar } from "./PlayerAvatar";
import { Flag } from "./Flag";

function Row({ children, testid }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-t border-[#1b1b1b] first:border-t-0" data-testid={testid}>
      {children}
    </div>
  );
}

function UserCell({ u }) {
  const p = u.player || {};
  const avatarPlayer = { id: p.player_id, avatar_url: p.avatar_url, display_name: u.display_name };
  return (
    <>
      <PlayerAvatar player={avatarPlayer} size={38} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="truncate text-slate-100 text-sm font-bold">{u.display_name}</span>
          {p.country?.iso3 && <Flag country={p.country} width={16} />}
        </div>
        <div className="text-[10px] font-mono text-slate-500 truncate">{p.wsbf_player_id || "—"}</div>
      </div>
    </>
  );
}

export default function FriendsSection() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [friends, setFriends] = useState([]);
  const [ownedClubs, setOwnedClubs] = useState([]);
  const [busyId, setBusyId] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [i, o, f, c] = await Promise.all([
        api.get("/friends/requests/incoming"),
        api.get("/friends/requests/outgoing"),
        api.get("/friends"),
        api.get("/clubs/mine/owned"),
      ]);
      setIncoming(i.data.requests || []);
      setOutgoing(o.data.requests || []);
      setFriends(f.data.friends || []);
      setOwnedClubs(c.data.clubs || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const latestQ = useRef("");
  latestQ.current = q.trim();
  const doSearch = async (silent = false) => {
    const term = q.trim();
    if (term.length < 2) { if (!silent) toast.error("Πληκτρολογήστε τουλάχιστον 2 χαρακτήρες"); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/users/search", { params: { q: term } });
      if (latestQ.current !== term) return; // stale: box changed/cleared while in flight
      setResults(data.results || []);
      if (!silent && (data.results || []).length === 0) toast.message("Δεν βρέθηκαν χρήστες");
    } catch (e) { toast.error(apiErr(e, "Search failed")); }
    finally { setSearching(false); }
  };
  // Live search as you type (2+ chars), debounced.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    const t = setTimeout(() => doSearch(true), 300);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendRequest = async (recipientUserId) => {
    setBusyId(recipientUserId);
    try {
      await api.post("/friends/requests", { recipient_user_id: recipientUserId });
      toast.success("Friend request sent");
      await refresh();
    } catch (e) { toast.error(apiErr(e, "Could not send request")); }
    finally { setBusyId(""); }
  };

  const act = async (fn, id, label) => {
    setBusyId(id);
    try { await fn(); toast.success(label); await refresh(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusyId(""); }
  };

  const accept = (id) => act(() => api.post(`/friends/requests/${id}/accept`), id, "Friend added");
  const decline = (id) => act(() => api.post(`/friends/requests/${id}/decline`), id, "Request declined");
  const cancel = (id) => act(() => api.delete(`/friends/requests/${id}`), id, "Request cancelled");
  const unfriend = (uid) => {
    if (!window.confirm("Remove this friend?")) return;
    return act(() => api.delete(`/friends/${uid}`), uid, "Friend removed");
  };
  const inviteToClub = (uid) =>
    act(() => api.post(`/friends/${uid}/invite-to-my-club`), uid, "Invitation sent to your friend");

  const myClub = useMemo(() => ownedClubs.find((c) => c.status !== "ARCHIVED") || null, [ownedClubs]);

  return (
    <Card className="p-6" data-testid="settings-friends">
      <div className="flex items-center gap-2 mb-1">
        <Users className="w-4 h-4 text-[#E5B842]" />
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50">ΦΙΛΟΙ / Friends</h2>
      </div>
      <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-5">
        Connect with other players · build your club roster from friends
      </p>

      {/* SEARCH */}
      <div className="mb-6">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              data-testid="friends-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Αναζήτηση με όνομα, WSTF ID ή ακριβές email…"
              className="w-full bg-[#0a0a0a] border border-[#2b2512] rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/50"
            />
          </div>
          <GoldButton onClick={doSearch} disabled={searching} data-testid="friends-search-submit">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </GoldButton>
        </div>
        {results.length > 0 && (
          <div className="mt-3 border border-[#1b1b1b] rounded-lg bg-[#0a0a0a] px-3" data-testid="friends-search-results">
            {results.map((u) => (
              <Row key={u.user_id} testid={`friends-search-result-${u.user_id}`}>
                <UserCell u={u} />
                {u.friendship_state === "FRIENDS" && (
                  <span className="text-[10px] font-mono uppercase text-emerald-400">Already friends</span>
                )}
                {u.friendship_state === "PENDING_OUT" && (
                  <span className="text-[10px] font-mono uppercase text-amber-300">Request sent</span>
                )}
                {u.friendship_state === "PENDING_IN" && (
                  <span className="text-[10px] font-mono uppercase text-sky-300">Awaiting your reply</span>
                )}
                {u.friendship_state === "NONE" && (
                  <GhostButton
                    onClick={() => sendRequest(u.user_id)}
                    disabled={busyId === u.user_id}
                    data-testid={`friends-send-request-${u.user_id}`}>
                    {busyId === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                    Add
                  </GhostButton>
                )}
              </Row>
            ))}
          </div>
        )}
      </div>

      {/* INCOMING */}
      <div className="mb-6">
        <h3 className="font-heading text-sm font-bold uppercase tracking-tight text-slate-200 mb-2 flex items-center gap-2">
          <MailIcon className="w-3.5 h-3.5 text-[#E5B842]" />
          Incoming requests <span className="text-[10px] font-mono text-slate-500">({incoming.length})</span>
        </h3>
        {incoming.length === 0 ? (
          <div className="text-xs font-mono text-slate-600 py-3" data-testid="friends-incoming-empty">No pending requests</div>
        ) : (
          <div className="border border-[#1b1b1b] rounded-lg bg-[#0a0a0a] px-3" data-testid="friends-incoming-list">
            {incoming.map((r) => (
              <Row key={r.id} testid={`friends-incoming-${r.id}`}>
                <UserCell u={r.sender} />
                <GhostButton onClick={() => decline(r.id)} disabled={busyId === r.id} data-testid={`friends-decline-${r.id}`}>
                  <X className="w-3.5 h-3.5" /> Decline
                </GhostButton>
                <GoldButton onClick={() => accept(r.id)} disabled={busyId === r.id} data-testid={`friends-accept-${r.id}`}>
                  {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Accept
                </GoldButton>
              </Row>
            ))}
          </div>
        )}
      </div>

      {/* OUTGOING */}
      <div className="mb-6">
        <h3 className="font-heading text-sm font-bold uppercase tracking-tight text-slate-200 mb-2 flex items-center gap-2">
          <Send className="w-3.5 h-3.5 text-[#E5B842]" />
          Pending outgoing <span className="text-[10px] font-mono text-slate-500">({outgoing.length})</span>
        </h3>
        {outgoing.length === 0 ? (
          <div className="text-xs font-mono text-slate-600 py-3" data-testid="friends-outgoing-empty">No outgoing requests</div>
        ) : (
          <div className="border border-[#1b1b1b] rounded-lg bg-[#0a0a0a] px-3" data-testid="friends-outgoing-list">
            {outgoing.map((r) => (
              <Row key={r.id} testid={`friends-outgoing-${r.id}`}>
                <UserCell u={r.recipient} />
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                <GhostButton onClick={() => cancel(r.id)} disabled={busyId === r.id} data-testid={`friends-cancel-${r.id}`}>
                  <X className="w-3.5 h-3.5" /> Cancel
                </GhostButton>
              </Row>
            ))}
          </div>
        )}
      </div>

      {/* FRIENDS LIST */}
      <div>
        <h3 className="font-heading text-sm font-bold uppercase tracking-tight text-slate-200 mb-2 flex items-center gap-2">
          <Users className="w-3.5 h-3.5 text-[#E5B842]" />
          My friends <span className="text-[10px] font-mono text-slate-500" data-testid="friends-count">({friends.length})</span>
        </h3>
        {!myClub && (
          <div className="text-[11px] font-mono text-slate-500 mb-2" data-testid="friends-club-hint">
            Χρειάζεσαι δικό σου club για προσκλήσεις —
            <Link to="/club-builder" className="text-[#E5B842] hover:underline ml-1">δημιούργησε ένα</Link>
          </div>
        )}
        {friends.length === 0 ? (
          <div className="text-xs font-mono text-slate-600 py-3" data-testid="friends-list-empty">No friends yet — search for players above</div>
        ) : (
          <div className="border border-[#1b1b1b] rounded-lg bg-[#0a0a0a] px-3" data-testid="friends-list">
            {friends.map((f) => (
              <Row key={f.user_id} testid={`friends-row-${f.user_id}`}>
                <UserCell u={f} />
                {f.player?.wsbf_player_id && (
                  <Link
                    to={`/players/${f.player.wsbf_player_id}`}
                    className="text-[10px] font-mono uppercase tracking-wider text-slate-400 hover:text-[#E5B842] px-2"
                    data-testid={`friends-passport-${f.user_id}`}>
                    Passport →
                  </Link>
                )}
                {myClub && (
                  <GhostButton
                    onClick={() => inviteToClub(f.user_id)}
                    disabled={busyId === f.user_id}
                    data-testid={`friends-invite-${f.user_id}`}>
                    {busyId === f.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                    Πρόσκληση στην ομάδα μου
                  </GhostButton>
                )}
                <button
                  onClick={() => unfriend(f.user_id)}
                  disabled={busyId === f.user_id}
                  className="text-xs text-slate-500 hover:text-rose-400 inline-flex items-center gap-1 px-2"
                  data-testid={`friends-unfriend-${f.user_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </Row>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
